/**
 * Signup attribution: first-touch UTM / referrer capture.
 *
 * Runs from src/index.js BEFORE React mounts, because two things in this app
 * destroy the query string and both happen after page entry:
 *   1. Google SSO uses `redirectTo: window.location.origin` (AuthModal.jsx),
 *      so a user who lands on /nfl?utm_campaign=x returns to a bare origin
 *      with the params gone. ~64% of users sign up via Google.
 *   2. handleGoHome does window.history.pushState({}, '', '/') (App.js).
 *
 * So: read the URL at page entry, stash it, and read it back from storage at
 * signup time. Every function here is silent - attribution must never block
 * or delay a signup.
 */

const STORAGE_KEY = 'kyg_attribution';
const SYNCED_KEY = 'kyg_attribution_synced';
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const MAX_LEN = 300;

const clip = (v) => (typeof v === 'string' ? v.trim().slice(0, MAX_LEN) : null);

/** Stored payload, or null if nothing has been captured yet. */
export function readAttribution() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (e) {
    return null;
  }
}

/**
 * Has this exact payload already been written for this user? Keyed on
 * captured_at so a direct-visit record that later upgrades to a real campaign
 * re-syncs, while a settled record stops re-writing on every cold load.
 */
export function isAttributionSynced(userId, attribution) {
  try {
    if (!userId || !attribution) return false;
    return localStorage.getItem(SYNCED_KEY) === `${userId}:${attribution.captured_at}`;
  } catch (e) {
    return false;
  }
}

export function markAttributionSynced(userId, attribution) {
  try {
    if (!userId || !attribution) return;
    localStorage.setItem(SYNCED_KEY, `${userId}:${attribution.captured_at}`);
  } catch (e) {
    // no-op
  }
}

/**
 * Capture on page entry. First-touch wins: an existing record is never
 * overwritten, EXCEPT when the stored record is a bare direct visit (no
 * source, no referrer). Direct is the absence of a source, not a source, so
 * a later real campaign click is allowed to claim credit.
 */
export function captureAttribution() {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    UTM_PARAMS.forEach((key) => {
      const value = clip(params.get(key));
      if (value) utm[key] = value;
    });
    const hasUtm = Object.keys(utm).length > 0;

    const existing = readAttribution();
    if (existing) {
      const existingHasSource = !!(
        existing.source ||
        (existing.referrer && existing.referrer !== 'direct')
      );
      // Locked to a real first touch, or nothing better on offer.
      if (existingHasSource || !hasUtm) return;
    }

    const referrer = clip(document.referrer);
    const payload = {
      source: utm.utm_source || null,
      medium: utm.utm_medium || null,
      campaign: utm.utm_campaign || null,
      content: utm.utm_content || null,
      term: utm.utm_term || null,
      landing_page: clip(window.location.pathname + window.location.search),
      // With no UTM and no referrer the visit is genuinely direct; record that
      // so organic/direct stays distinguishable from "never captured".
      referrer: referrer || (hasUtm ? null : 'direct'),
      captured_at: new Date().toISOString(),
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (e) {
    // localStorage disabled, malformed URL, anything: stay silent.
  }
}
